/**
 * Geo resolution for threat map pins.
 *
 * City coords are bulk-generated from every distinct `sessions.source_city`
 * in production D1 (see scripts/regen-city-coords.mjs). Prefer exact city
 * match; fall back to country centroid when the city name is absent or new.
 */

import generatedCityCoords from './city-coords.generated.json';

export type Coords = { lat: number; lng: number };

/** All recorded session cities (+ aliases). Source: D1 + Open-Meteo/manual. */
export const CITY_COORDS: Record<string, Coords> = generatedCityCoords as Record<
  string,
  Coords
>;

/** ISO-2 and common full names → approximate country centroids */
export const COUNTRY_COORDS: Record<string, Coords> = {
  US: { lat: 39.8283, lng: -98.5795 },
  USA: { lat: 39.8283, lng: -98.5795 },
  'United States': { lat: 39.8283, lng: -98.5795 },
  CA: { lat: 56.1304, lng: -106.3468 },
  Canada: { lat: 56.1304, lng: -106.3468 },
  GB: { lat: 55.3781, lng: -3.436 },
  UK: { lat: 55.3781, lng: -3.436 },
  'United Kingdom': { lat: 55.3781, lng: -3.436 },
  DE: { lat: 51.1657, lng: 10.4515 },
  Germany: { lat: 51.1657, lng: 10.4515 },
  FR: { lat: 46.2276, lng: 2.2137 },
  France: { lat: 46.2276, lng: 2.2137 },
  NL: { lat: 52.1326, lng: 5.2913 },
  Netherlands: { lat: 52.1326, lng: 5.2913 },
  RU: { lat: 61.524, lng: 105.3188 },
  Russia: { lat: 61.524, lng: 105.3188 },
  CN: { lat: 35.8617, lng: 104.1954 },
  China: { lat: 35.8617, lng: 104.1954 },
  JP: { lat: 36.2048, lng: 138.2529 },
  Japan: { lat: 36.2048, lng: 138.2529 },
  KR: { lat: 35.9078, lng: 127.7669 },
  'South Korea': { lat: 35.9078, lng: 127.7669 },
  IN: { lat: 20.5937, lng: 78.9629 },
  India: { lat: 20.5937, lng: 78.9629 },
  BR: { lat: -14.235, lng: -51.9253 },
  Brazil: { lat: -14.235, lng: -51.9253 },
  AU: { lat: -25.2744, lng: 133.7751 },
  Australia: { lat: -25.2744, lng: 133.7751 },
  SG: { lat: 1.3521, lng: 103.8198 },
  Singapore: { lat: 1.3521, lng: 103.8198 },
  HK: { lat: 22.3193, lng: 114.1694 },
  'Hong Kong': { lat: 22.3193, lng: 114.1694 },
  IE: { lat: 53.1424, lng: -7.6921 },
  Ireland: { lat: 53.1424, lng: -7.6921 },
  SE: { lat: 60.1282, lng: 18.6435 },
  Sweden: { lat: 60.1282, lng: 18.6435 },
  PL: { lat: 51.9194, lng: 19.1451 },
  Poland: { lat: 51.9194, lng: 19.1451 },
  IT: { lat: 41.8719, lng: 12.5674 },
  Italy: { lat: 41.8719, lng: 12.5674 },
  ES: { lat: 40.4637, lng: -3.7492 },
  Spain: { lat: 40.4637, lng: -3.7492 },
  UA: { lat: 48.3794, lng: 31.1656 },
  Ukraine: { lat: 48.3794, lng: 31.1656 },
  TR: { lat: 38.9637, lng: 35.2433 },
  Turkey: { lat: 38.9637, lng: 35.2433 },
  IL: { lat: 31.0461, lng: 34.8516 },
  Israel: { lat: 31.0461, lng: 34.8516 },
  AE: { lat: 23.4241, lng: 53.8478 },
  'United Arab Emirates': { lat: 23.4241, lng: 53.8478 },
  ZA: { lat: -30.5595, lng: 22.9375 },
  'South Africa': { lat: -30.5595, lng: 22.9375 },
  MX: { lat: 23.6345, lng: -102.5528 },
  Mexico: { lat: 23.6345, lng: -102.5528 },
  VN: { lat: 14.0583, lng: 108.2772 },
  Vietnam: { lat: 14.0583, lng: 108.2772 },
  TH: { lat: 15.87, lng: 100.9925 },
  Thailand: { lat: 15.87, lng: 100.9925 },
  ID: { lat: -0.7893, lng: 113.9213 },
  Indonesia: { lat: -0.7893, lng: 113.9213 },
  PH: { lat: 12.8797, lng: 121.774 },
  Philippines: { lat: 12.8797, lng: 121.774 },
  TW: { lat: 23.6978, lng: 120.9605 },
  Taiwan: { lat: 23.6978, lng: 120.9605 },
  RO: { lat: 45.9432, lng: 24.9668 },
  Romania: { lat: 45.9432, lng: 24.9668 },
  CZ: { lat: 49.8175, lng: 15.473 },
  'Czech Republic': { lat: 49.8175, lng: 15.473 },
  Czechia: { lat: 49.8175, lng: 15.473 },
  FI: { lat: 61.9241, lng: 25.7482 },
  Finland: { lat: 61.9241, lng: 25.7482 },
  NO: { lat: 60.472, lng: 8.4689 },
  Norway: { lat: 60.472, lng: 8.4689 },
  PT: { lat: 39.3999, lng: -8.2245 },
  Portugal: { lat: 39.3999, lng: -8.2245 },
  BE: { lat: 50.5039, lng: 4.4699 },
  Belgium: { lat: 50.5039, lng: 4.4699 },
  CH: { lat: 46.8182, lng: 8.2275 },
  Switzerland: { lat: 46.8182, lng: 8.2275 },
  AT: { lat: 47.5162, lng: 14.5501 },
  Austria: { lat: 47.5162, lng: 14.5501 },
  HU: { lat: 47.1625, lng: 19.5033 },
  Hungary: { lat: 47.1625, lng: 19.5033 },
  BG: { lat: 42.7339, lng: 25.4858 },
  Bulgaria: { lat: 42.7339, lng: 25.4858 },
  NZ: { lat: -40.9006, lng: 174.886 },
  'New Zealand': { lat: -40.9006, lng: 174.886 },
  AR: { lat: -38.4161, lng: -63.6167 },
  Argentina: { lat: -38.4161, lng: -63.6167 },
  CL: { lat: -35.6751, lng: -71.543 },
  Chile: { lat: -35.6751, lng: -71.543 },
  CO: { lat: 4.5709, lng: -74.2973 },
  Colombia: { lat: 4.5709, lng: -74.2973 },
  PE: { lat: -9.19, lng: -75.0152 },
  Peru: { lat: -9.19, lng: -75.0152 },
  EG: { lat: 26.8206, lng: 30.8025 },
  Egypt: { lat: 26.8206, lng: 30.8025 },
  NG: { lat: 9.082, lng: 8.6753 },
  Nigeria: { lat: 9.082, lng: 8.6753 },
  KE: { lat: -0.0236, lng: 37.9062 },
  Kenya: { lat: -0.0236, lng: 37.9062 },
  SA: { lat: 23.8859, lng: 45.0792 },
  'Saudi Arabia': { lat: 23.8859, lng: 45.0792 },
  PK: { lat: 30.3753, lng: 69.3451 },
  Pakistan: { lat: 30.3753, lng: 69.3451 },
  BD: { lat: 23.685, lng: 90.3563 },
  Bangladesh: { lat: 23.685, lng: 90.3563 },
  MY: { lat: 4.2105, lng: 101.9758 },
  Malaysia: { lat: 4.2105, lng: 101.9758 },
  LT: { lat: 55.1694, lng: 23.8813 },
  Lithuania: { lat: 55.1694, lng: 23.8813 },
  LV: { lat: 56.8796, lng: 24.6032 },
  Latvia: { lat: 56.8796, lng: 24.6032 },
  EE: { lat: 58.5953, lng: 25.0136 },
  Estonia: { lat: 58.5953, lng: 25.0136 },
  DK: { lat: 56.2639, lng: 9.5018 },
  Denmark: { lat: 56.2639, lng: 9.5018 },
  IQ: { lat: 33.2232, lng: 43.6793 },
  Iraq: { lat: 33.2232, lng: 43.6793 },
  IR: { lat: 32.4279, lng: 53.688 },
  Iran: { lat: 32.4279, lng: 53.688 },
  NP: { lat: 28.3949, lng: 84.124 },
  Nepal: { lat: 28.3949, lng: 84.124 },
  KH: { lat: 12.5657, lng: 104.991 },
  Cambodia: { lat: 12.5657, lng: 104.991 },
  MA: { lat: 31.7917, lng: -7.0926 },
  Morocco: { lat: 31.7917, lng: -7.0926 },
  AZ: { lat: 40.1431, lng: 47.5769 },
  Azerbaijan: { lat: 40.1431, lng: 47.5769 },
  JO: { lat: 30.5852, lng: 36.2384 },
  Jordan: { lat: 30.5852, lng: 36.2384 },
  LU: { lat: 49.8153, lng: 6.1296 },
  Luxembourg: { lat: 49.8153, lng: 6.1296 },
  SI: { lat: 46.1512, lng: 14.9955 },
  Slovenia: { lat: 46.1512, lng: 14.9955 },
  MD: { lat: 47.4116, lng: 28.3699 },
  Moldova: { lat: 47.4116, lng: 28.3699 },
  RS: { lat: 44.0165, lng: 21.0059 },
  Serbia: { lat: 44.0165, lng: 21.0059 },
  BA: { lat: 43.9159, lng: 17.6791 },
  'Bosnia and Herzegovina': { lat: 43.9159, lng: 17.6791 },
  HR: { lat: 45.1, lng: 15.2 },
  Croatia: { lat: 45.1, lng: 15.2 },
  SK: { lat: 48.669, lng: 19.699 },
  Slovakia: { lat: 48.669, lng: 19.699 },
  GR: { lat: 39.0742, lng: 21.8243 },
  Greece: { lat: 39.0742, lng: 21.8243 },
  KZ: { lat: 48.0196, lng: 66.9237 },
  Kazakhstan: { lat: 48.0196, lng: 66.9237 },
  UZ: { lat: 41.3775, lng: 64.5853 },
  Uzbekistan: { lat: 41.3775, lng: 64.5853 },
  GE: { lat: 42.3154, lng: 43.3569 },
  Georgia: { lat: 42.3154, lng: 43.3569 },
  SY: { lat: 34.8021, lng: 38.9968 },
  Syria: { lat: 34.8021, lng: 38.9968 },
  QA: { lat: 25.3548, lng: 51.1839 },
  Qatar: { lat: 25.3548, lng: 51.1839 },
  KW: { lat: 29.3117, lng: 47.4818 },
  Kuwait: { lat: 29.3117, lng: 47.4818 },
  OM: { lat: 21.4735, lng: 55.9754 },
  Oman: { lat: 21.4735, lng: 55.9754 },
  MM: { lat: 21.9162, lng: 95.956 },
  Myanmar: { lat: 21.9162, lng: 95.956 },
  ET: { lat: 9.145, lng: 40.4897 },
  Ethiopia: { lat: 9.145, lng: 40.4897 },
  TZ: { lat: -6.369, lng: 34.8888 },
  Tanzania: { lat: -6.369, lng: 34.8888 },
  DZ: { lat: 28.0339, lng: 1.6596 },
  Algeria: { lat: 28.0339, lng: 1.6596 },
  TN: { lat: 33.8869, lng: 9.5375 },
  Tunisia: { lat: 33.8869, lng: 9.5375 },
  EC: { lat: -1.8312, lng: -78.1834 },
  Ecuador: { lat: -1.8312, lng: -78.1834 },
  VE: { lat: 6.4238, lng: -66.5897 },
  Venezuela: { lat: 6.4238, lng: -66.5897 },
  SV: { lat: 13.7942, lng: -88.8965 },
  'El Salvador': { lat: 13.7942, lng: -88.8965 },
  HN: { lat: 15.2, lng: -86.2419 },
  Honduras: { lat: 15.2, lng: -86.2419 },
  NI: { lat: 12.8654, lng: -85.2072 },
  Nicaragua: { lat: 12.8654, lng: -85.2072 },
  PR: { lat: 18.2208, lng: -66.5901 },
  'Puerto Rico': { lat: 18.2208, lng: -66.5901 },
  BY: { lat: 53.7098, lng: 27.9534 },
  Belarus: { lat: 53.7098, lng: 27.9534 },
  AL: { lat: 41.1533, lng: 20.1683 },
  Albania: { lat: 41.1533, lng: 20.1683 },
  NA: { lat: -22.9576, lng: 18.4904 },
  Namibia: { lat: -22.9576, lng: 18.4904 },
  CM: { lat: 7.3697, lng: 12.3547 },
  Cameroon: { lat: 7.3697, lng: 12.3547 },
  AD: { lat: 42.5063, lng: 1.5218 },
  Andorra: { lat: 42.5063, lng: 1.5218 },
};

function normalizeKey(value: string | null | undefined): string {
  return (value || '').trim();
}

/** Normalize unicode so curly quotes / diacritics still match. */
function foldKey(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining marks
    .replace(/[’‘`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Strip common geo suffixes so "Nanjing City" still matches. */
function cityLookupKeys(city: string): string[] {
  const base = city.trim();
  if (!base) return [];
  const keys = new Set<string>([base]);
  const stripped = base
    .replace(/\s+(city|shi|municipality|prefecture|province|region)$/i, '')
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped) keys.add(stripped);
  return [...keys];
}

// Lazy case-folded index over CITY_COORDS (built once)
let cityFoldIndex: Map<string, Coords> | null = null;
function getCityFoldIndex(): Map<string, Coords> {
  if (!cityFoldIndex) {
    cityFoldIndex = new Map();
    for (const [k, v] of Object.entries(CITY_COORDS)) {
      cityFoldIndex.set(foldKey(k), v);
    }
  }
  return cityFoldIndex;
}

function lookupCity(city: string): Coords | null {
  if (!city) return null;
  for (const key of cityLookupKeys(city)) {
    if (CITY_COORDS[key]) return CITY_COORDS[key];
    const folded = getCityFoldIndex().get(foldKey(key));
    if (folded) return folded;
  }
  return null;
}

function lookupCountry(country: string): Coords | null {
  if (!country) return null;
  if (COUNTRY_COORDS[country]) return COUNTRY_COORDS[country];
  const upper = country.toUpperCase();
  if (COUNTRY_COORDS[upper]) return COUNTRY_COORDS[upper];
  const lower = country.toLowerCase();
  for (const [k, v] of Object.entries(COUNTRY_COORDS)) {
    if (k.toLowerCase() === lower) return v;
  }
  return null;
}

export type GeoResolution = {
  coords: Coords | null;
  /** city | country | none */
  precision: 'city' | 'country' | 'none';
  /** True when feed has a city name but we only have country-level coords */
  cityNameUnmapped?: boolean;
  cityName?: string;
};

export function resolveThreatGeo(
  city?: string | null,
  country?: string | null,
): GeoResolution {
  const c = normalizeKey(city);
  const co = normalizeKey(country);
  const cityHit = lookupCity(c);
  if (cityHit) return { coords: cityHit, precision: 'city', cityName: c || undefined };
  const countryHit = lookupCountry(co);
  if (countryHit) {
    return {
      coords: countryHit,
      precision: 'country',
      cityNameUnmapped: !!c,
      cityName: c || undefined,
    };
  }
  return { coords: null, precision: 'none', cityName: c || undefined };
}

export function isMappable(city?: string | null, country?: string | null): boolean {
  return resolveThreatGeo(city, country).coords !== null;
}

/** Operator-facing note when pin is approximate. */
export function geoApproxNote(geo: GeoResolution): string | null {
  if (geo.precision !== 'country') return null;
  if (geo.cityNameUnmapped && geo.cityName) {
    return `No map pin for ${geo.cityName} yet — showing country centroid`;
  }
  return 'City unknown — showing country centroid';
}

/** Count of cities in the generated atlas (for diagnostics). */
export function cityAtlasSize(): number {
  return Object.keys(CITY_COORDS).length;
}
